import {
  ProductLinesOfBusiness,
  ProductTypes,
  type ProductType,
} from '../../../../types/products';

export interface ProductSchema {
  productType: string;
  productName: string;
  lineOfBusiness: ProductLinesOfBusiness;
  coverageLimit: string;
  eligibilityItems: string[];
  coverageHighlights: string[];
  productFeatures: string[];
  ordinal: number;
}

/**
 * P250 and P250X are one product offering to a broker, so they share one card
 * definition rather than two copies of the same copy. Referencing it from both
 * keys is what makes them indistinguishable — there is no second literal to
 * drift out of step and start telling a broker which key they are on. Only one
 * of the two is ever rendered; FPX-8684 gates them on the cutover flag.
 */
const PRIME_250_SCHEMA: ProductSchema = {
  productType: 'Cyber',
  productName: 'PRIME 250',
  lineOfBusiness: ProductLinesOfBusiness.CYBER,
  coverageLimit: 'Limits up to $5 million',
  eligibilityItems: [
    'Revenue up to $250M',
    'Available to organizations with limited prior cyber claims',
  ],
  coverageHighlights: [
    'First & Third Party Coverage',
    'Cyber Crime Available; including invoice manipulation, cryptojacking, telecommunications hack & more',
    'Business Interruption & Contingent Business Interruption Full limits available',
    'System Failure & Cyber Event Coverage',
  ],
  productFeatures: [
    'Surplus Lines',
    'AM Best A-VII Rated (or Higher)',
    'Underwriting Review Available',
  ],
  ordinal: 2,
};

export const PRODUCT_SCHEMAS: Record<ProductType, ProductSchema> = {
  [ProductTypes.p100]: {
    productType: 'Cyber',
    productName: 'PRIME 100',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: 'Limits up to $3 million',
    eligibilityItems: [
      'Revenue up to $100M',
      'For Accounts with No Prior Claims',
    ],
    coverageHighlights: [
      'First & Third Party Coverage',
      'Cyber Crime Available; including invoice manipulation, cryptojacking & more',
      'Contingent Business Interruption',
    ],
    productFeatures: [
      'Admitted',
      'AM Best A-VIII Rated',
      'Automated Underwriting',
    ],
    ordinal: 1,
  },
  [ProductTypes.p250]: PRIME_250_SCHEMA,
  [ProductTypes.p250x]: PRIME_250_SCHEMA,
  [ProductTypes.p100_pro]: {
    productType: 'Cyber',
    productName: 'PRIME 100 PRO',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: 'Limits up to $3 million',
    eligibilityItems: [
      'Revenue up to $100M',
      'For Accounts with No Prior Claims',
    ],
    coverageHighlights: [
      'First & Third Party Coverage',
      'Cyber Crime Available; including invoice manipulation, cryptojacking & more',
      'Business Income & Extra Expense',
      'Contingent Business Interruption',
      'Impersonation Fraud',
    ],
    productFeatures: [
      'Admitted',
      'AM Best A-X Rated',
      'Automated Underwriting with Instant Quotes',
      'No Waiting Period',
    ],
    ordinal: 3,
  },
  [ProductTypes.prime_tech]: {
    productType: 'Cyber',
    productName: 'PRIME TECH',
    lineOfBusiness: ProductLinesOfBusiness.TECH_ENO,
    coverageLimit: 'Limits up to $5 million',
    eligibilityItems: [],
    coverageHighlights: [
      'Technology E&O and Cyber Insurance Coverage',
      'First & Third Party Coverage',
      'Cyber Crime Available',
    ],
    productFeatures: ['Surplus Lines'],
    ordinal: 4,
  },
  [ProductTypes.prime_one]: {
    productType: 'Cyber',
    productName: 'PRIME ONE',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: 'Limits up to £5 million',
    eligibilityItems: [],
    coverageHighlights: [
      'Breach Response costs',
      'Cyber Crime',
      'Business Interruption (incl. System Failure)',
      'Extortion costs',
      'Liability costs',
      'Media Liability',
      'Hardware replacement (Bricking) costs',
      'Data restoration costs',
    ],
    productFeatures: [],
    ordinal: 5,
  },
  [ProductTypes.prime_one_tech]: {
    productType: 'Cyber',
    productName: 'PRIME ONE TECH',
    lineOfBusiness: ProductLinesOfBusiness.TECH_ENO,
    coverageLimit: 'Limits up to £5 million',
    eligibilityItems: [],
    coverageHighlights: [
      'Technology Services Liability',
      'Subcontractors work',
      'Media liability',
      'Mitigation costs',
      'Payment of withheld fees',
      'Intellectual Property Right Infringement',
      'First and Third Party Cyber coverage',
      'Cyber Crime',
    ],
    productFeatures: [],
    ordinal: 6,
  },
  [ProductTypes.p250S]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 100,
  },
  [ProductTypes.prime_one_au]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 102,
  },
  [ProductTypes.prime_one_us]: {
    productType: 'Cyber',
    productName: 'PRIME ONE',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: 'Limits up to $5 million',
    eligibilityItems: [
      'Revenue up to $250M',
      'Available to organizations with prior claims',
    ],
    coverageHighlights: [
      'First & Third Party Coverage',
      'Cyber Crime Available; including invoice manipulation, cryptojacking, telecommunications hack & more',
      'Business Interruption & Contingent Business Interruption Full limits available',
      'System Failure & Cyber Event Coverage',
      'AI and Quantum Computer Incidents Coverage',
    ],
    productFeatures: [
      'Surplus Lines',
      'AM Best A Rated',
      'Underwriting Review Available',
    ],
    ordinal: 7,
  },
  [ProductTypes.p_plus]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 103,
  },
  [ProductTypes.prime_tech_plus]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.TECH_ENO,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 104,
  },
  [ProductTypes.p_one_plus]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 105,
  },
  [ProductTypes.prime_one_tech_plus]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.TECH_ENO,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 106,
  },
  [ProductTypes.zsp]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.MANAGEMENT_LIABILITY,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 107,
  },
  [ProductTypes.prime_one_plus_us]: {
    productType: 'Cyber',
    productName: 'Not Available',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 108,
  },
  [ProductTypes.notSupported]: {
    productType: 'Cyber',
    productName: 'Not Supported',
    lineOfBusiness: ProductLinesOfBusiness.CYBER,
    coverageLimit: '',
    eligibilityItems: [],
    coverageHighlights: [],
    productFeatures: [],
    ordinal: 999,
  },
};
